import type { IPost } from "../../dashboard/types";
import { dataCurta, diasEntre, paraData, primeiraFrase } from "./formatar";

export type AvaliacaoDoRelatorio = {
  starRating: string;
  comment: string | null;
  createTime: string;
  userName: string;
  replied?: boolean;
  reply?: string | null;
  repliedAt?: string | null;
};

export type ItemDaLinhaDoTempo = {
  chave: string;
  quando: Date;
  data: string;
  tipo: "impulsionamento" | "resposta";
  rotulo: string;
  titulo: string;
};

const dentroDoPeriodo = (quando: Date | null, dias: number, hoje: Date): quando is Date =>
  quando !== null && diasEntre(quando, hoje) <= dias && quando.getTime() <= hoje.getTime() + 86_400_000;

export const impulsionamentosDoPeriodo = (
  posts: IPost[] | null | undefined,
  dias?: number,
  hoje: Date = new Date(),
): IPost[] =>
  [...(posts ?? [])]
    .filter((post) => {
      const quando = paraData(post.createTime);
      return quando !== null && (dias === undefined || dentroDoPeriodo(quando, dias, hoje));
    })
    .sort((a, b) => (paraData(b.createTime)?.getTime() ?? 0) - (paraData(a.createTime)?.getTime() ?? 0));

export const respostasDoPeriodo = (
  reviews: AvaliacaoDoRelatorio[] | null | undefined,
  dias: number,
  hoje: Date = new Date(),
): AvaliacaoDoRelatorio[] =>
  (reviews ?? []).filter((review) => review.replied && dentroDoPeriodo(paraData(review.repliedAt), dias, hoje));

export const linhaDoTempo = (
  posts: IPost[] | null | undefined,
  reviews: AvaliacaoDoRelatorio[] | null | undefined,
  dias: number,
  hoje: Date = new Date(),
  limite = 3,
): ItemDaLinhaDoTempo[] => {
  const itens: ItemDaLinhaDoTempo[] = [];
  impulsionamentosDoPeriodo(posts).forEach((post, indice) => {
    const quando = paraData(post.createTime);
    if (!dentroDoPeriodo(quando, dias, hoje)) return;
    itens.push({
      chave: `post-${indice}-${post.createTime}`,
      quando,
      data: dataCurta(quando) ?? "",
      tipo: "impulsionamento",
      rotulo: "Impulsionamento publicado",
      titulo: primeiraFrase(post.summary || "Impulsionamento no seu perfil"),
    });
  });
  respostasDoPeriodo(reviews, dias, hoje).forEach((review, indice) => {
    const quando = paraData(review.repliedAt) as Date;
    itens.push({
      chave: `resposta-${indice}-${review.repliedAt}`,
      quando,
      data: dataCurta(quando) ?? "",
      tipo: "resposta",
      rotulo: "Avaliação respondida",
      titulo: `Resposta publicada para a avaliação de ${review.userName || "um cliente"}`,
    });
  });
  return itens.sort((a, b) => b.quando.getTime() - a.quando.getTime()).slice(0, limite);
};

export const avaliacaoEmDestaque = (
  reviews: AvaliacaoDoRelatorio[] | null | undefined,
): AvaliacaoDoRelatorio | null => {
  const lista = [...(reviews ?? [])].sort(
    (a, b) => (paraData(b.createTime)?.getTime() ?? 0) - (paraData(a.createTime)?.getTime() ?? 0),
  );
  return lista.find((review) => review.replied && review.reply) ?? lista[0] ?? null;
};

export const estrelas = (starRating: string): number =>
  ({ ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 })[starRating] ?? 0;

export const diasSemImpulsionamento = (
  posts: IPost[] | null | undefined,
  hoje: Date = new Date(),
): number | null => {
  const ultimo = impulsionamentosDoPeriodo(posts)[0];
  const quando = ultimo ? paraData(ultimo.createTime) : null;
  return quando ? Math.max(0, diasEntre(quando, hoje)) : null;
};
